import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Sparkles, BookOpen, ThumbsUp, ThumbsDown, Archive, ArchiveRestore, CheckCircle2, AlertTriangle, Ruler } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Empty } from "@/components/app/Common";
import { useLoad } from "@/hooks/useLoad";
import { api, errMsg } from "@/lib/api";
import { fmtDate } from "@/lib/ui";

const TYPES = [["proven_play", "Proven plays", CheckCircle2, "text-emerald-500"], ["common_mistake", "Common mistakes", AlertTriangle, "text-red-500"], ["rule", "Rules of thumb", Ruler, "text-blue-500"]];
const CONF = { high: "bg-emerald-500/12 text-emerald-600 dark:text-emerald-400", medium: "bg-amber-500/12 text-amber-600 dark:text-amber-400", low: "bg-slate-500/15 text-slate-600 dark:text-slate-300" };

export const Helpful = ({ feature, itemId, initial }) => {
  const [v, setV] = useState(initial ?? null);
  const send = async (helpful) => {
    setV(helpful);
    try {
      await api.post("/feedback", { feature, item_id: itemId || "", helpful });
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground" data-testid={`helpful-${feature}-${itemId || "x"}`}>Helpful?
      <button type="button" onClick={() => send(true)} aria-label="Helpful" className={`p-1 rounded hover:bg-accent ${v === true ? "text-emerald-500" : ""}`} data-testid={`helpful-yes-${itemId || feature}`}><ThumbsUp className="h-3.5 w-3.5" /></button>
      <button type="button" onClick={() => send(false)} aria-label="Not helpful" className={`p-1 rounded hover:bg-accent ${v === false ? "text-red-500" : ""}`} data-testid={`helpful-no-${itemId || feature}`}><ThumbsDown className="h-3.5 w-3.5" /></button>
    </span>
  );
};

const Play = ({ p, owner, reload, how }) => {
  const archive = async () => {
    try {
      await api.put(`/playbook/plays/${p.id}`, { archived: !p.archived });
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className={`rounded-lg border border-border p-3.5 ${p.archived ? "opacity-50" : ""}`} data-testid={`play-${p.id}`}>
      <div className="flex flex-wrap items-center gap-2"><b className="text-sm flex-1 min-w-[200px]">{p.title}</b>
        <span className="text-[11px] text-muted-foreground capitalize">{p.category}</span><span className={`text-[11px] rounded-full px-2 py-0.5 ${CONF[p.confidence]}`}>{p.confidence} confidence</span></div>
      {how ? <p className="text-sm mt-1">{how}</p> : <>
        {p.applies_when && <p className="text-xs text-muted-foreground mt-1"><b>When:</b> {p.applies_when}</p>}
        <p className="text-sm mt-1">{p.guidance}</p>
        {p.checklist?.length > 0 && <ul className="mt-2 space-y-0.5 text-xs text-muted-foreground list-disc pl-5">{p.checklist.map((c) => <li key={c}>{c}</li>)}</ul>}
        {p.decisions?.length > 0 && <p className="text-[11px] text-muted-foreground mt-2">Evidence: {p.decisions.map((d) => d.title).join(" · ")}</p>}
        {p.source && <p className="text-[11px] text-muted-foreground mt-1" data-testid={`play-source-${p.id}`}>From: {p.source}</p>}
        <div className="flex items-center gap-3 mt-2"><Helpful feature="playbook" itemId={p.id} initial={p.helpful} />
          {owner && <button onClick={archive} className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground" data-testid={`play-archive-${p.id}`}>{p.archived ? <><ArchiveRestore className="h-3.5 w-3.5" />Restore</> : <><Archive className="h-3.5 w-3.5" />Retire play</>}</button>}</div>
      </>}
    </div>
  );
};

export const PlaybookPanel = ({ owner }) => {
  const state = useLoad(() => api.get("/playbook").then((r) => r.data));
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      await api.post("/playbook/generate");
      state.reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy(false);
  };
  const pb = state.data;
  return (
    <div data-testid="playbook-panel">
      <div className="surface p-6 mb-6 border-l-4 border-primary/50 flex flex-wrap items-center justify-between gap-3">
        <div className="max-w-2xl"><div className="flex items-center gap-2 font-semibold"><BookOpen className="h-4 w-4 text-primary" />Your Decision Playbook
          {pb && <span className="text-xs font-normal text-muted-foreground">· from {pb.based_on} decisions ({pb.reviewed} with results) · {fmtDate(pb.created_at)}</span>}</div>
          <p className="text-sm text-muted-foreground mt-1" data-testid="playbook-summary">{pb?.summary || "Claude turns your past decisions and outcomes into proven plays, mistakes to avoid and rules of thumb — then brings them up when a similar decision comes along."}</p></div>
        {owner && <Button onClick={run} disabled={busy} data-testid="playbook-generate-btn">{busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1.5" />}{pb ? "Rebuild playbook" : "Build my playbook"}</Button>}
      </div>
      {state.error && <p className="text-sm text-muted-foreground">{state.error}</p>}
      {pb?.plays?.length ? TYPES.map(([t, label, Icon, color]) => {
        const items = pb.plays.filter((p) => p.type === t);
        return items.length ? (
          <div key={t} className="mb-6" data-testid={`playbook-group-${t}`}>
            <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2"><Icon className={`h-3.5 w-3.5 ${color}`} />{label} · {items.length}</div>
            <div className="grid gap-3 md:grid-cols-2">{items.map((p) => <Play key={p.id} p={p} owner={owner} reload={state.reload} />)}</div>
          </div>) : null;
      }) : !state.loading && <Empty icon={BookOpen} title="No playbook yet" text="Log results on a few decisions, then build your playbook. It gets sharper with every outcome you record." testId="playbook-empty" />}
    </div>
  );
};

export const PlaybookCheck = ({ situation, category, dealId, label = "Check against my playbook" }) => {
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      setRes((await api.post("/playbook/check", { situation, category: category || "other", deal_id: dealId || null })).data);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || errMsg(e));
    }
    setBusy(false);
  };
  return (
    <div className="rounded-lg border border-border p-3" data-testid="playbook-check">
      <Button type="button" size="sm" variant="outline" onClick={run} disabled={busy || (situation || "").trim().length < 3} data-testid="playbook-check-btn">{busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <BookOpen className="h-3.5 w-3.5 mr-1" />}{label}</Button>
      {res && (res.plays.length ? (
        <div className="mt-3 space-y-2 text-sm" data-testid="playbook-check-results">
          {res.guidance && <p className="flex gap-2"><Sparkles className="h-4 w-4 text-primary mt-0.5 shrink-0" />{res.guidance}</p>}
          {res.watch_out && <p className="flex gap-2 text-red-600 dark:text-red-400 text-xs"><AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />{res.watch_out}</p>}
          {res.plays.map((p) => <Play key={p.id} p={p} how={p.how} />)}
          <Helpful feature="playbook_check" itemId={dealId || "decision"} />
        </div>) : <p className="mt-2 text-xs text-muted-foreground" data-testid="playbook-check-none">{res.empty ? "No playbook yet — build one from Decision Intelligence → Playbook." : "None of your plays apply here."}</p>)}
    </div>
  );
};
